import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { authFetch } from './api';
import ClaudeMemoryImport, { readClaudeMemory } from './ClaudeMemoryImport';
import './NextExperience.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const NAV_ITEMS = [
  { id: 'today', label: 'Today', icon: '⌁' },
  { id: 'missions', label: 'Missions', icon: '◫' },
  { id: 'library', label: 'Library', icon: '◇' },
];

const INTENTS = [
  { id: 'explore', label: 'Explore', mode: 'idea', icon: '✦', hint: 'Find a wedge · Opportunity model' },
  { id: 'decide', label: 'Decide', mode: 'decision', icon: '⌘', hint: 'Choose clearly · Decision brief' },
  { id: 'write', label: 'Write', mode: 'writing', icon: '¶', hint: 'Persuade · Structured draft' },
  { id: 'plan', label: 'Plan', mode: 'plan', icon: '▰', hint: 'Sequence work · Execution plan' },
  { id: 'learn', label: 'Learn', mode: 'learn', icon: '◈', hint: 'Build mastery · Mastery map' },
  { id: 'code', label: 'Code', mode: 'codebase', icon: '⟨/⟩', hint: 'Trace a system · System map' },
  { id: 'career', label: 'Career', mode: 'resume', icon: '◎', hint: 'Make your case · Application strategy' },
];

const MODE_META = {
  idea: { label: 'Explore', color: '#8b7cff' },
  decision: { label: 'Decide', color: '#ffb86b' },
  plan: { label: 'Build', color: '#57d7a3' },
  learn: { label: 'Learn', color: '#63c7ff' },
  writing: { label: 'Write', color: '#d99cff' },
  codebase: { label: 'Code', color: '#7bdcf4' },
  resume: { label: 'Career', color: '#74c0fc' },
};

function readLocalSessions() {
  try {
    const idea = JSON.parse(localStorage.getItem('IDEA_CANVAS_SESSIONS') || '[]');
    const code = JSON.parse(localStorage.getItem('CODEBASE_CANVAS_SESSIONS') || '[]');
    return [...idea, ...code].map(session => ({
      ...session,
      idea: session.idea || session.label || 'Untitled mission',
      mode: session.mode || 'idea',
      source: session.source || 'local',
      updatedAt: session.updatedAt || (session.timestamp ? new Date(session.timestamp).toISOString() : session.id),
      nodeCount: session.nodeCount || session.rawNodes?.length || 0,
    }));
  } catch {
    return [];
  }
}

function normalizeSessions(serverSessions, localSessions) {
  const seen = new Set();
  return [...serverSessions, ...localSessions]
    .filter(session => {
      const key = (session.idea || session.label || '').trim().toLowerCase().slice(0, 80);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));
}

function relativeTime(value) {
  if (!value) return 'Recently';
  const diff = Date.now() - new Date(value).getTime();
  if (!Number.isFinite(diff) || diff < 0) return 'Recently';
  const minutes = Math.floor(diff / 60000);
  if (minutes < 2) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'Yesterday' : `${days}d ago`;
}

function lifecycleStage(session) {
  if (session.meta?.intake?.verification?.results?.length) return 'Learn';
  if ((session.artifacts?.length || 0) > 0) return 'Act';
  if ((session.debates?.length || 0) > 0) return 'Challenge';
  if ((session.nodeCount || session.rawNodes?.length || session.nodes?.length || 0) > 0) return 'Explore';
  return 'Frame';
}

function cleanTitle(session) {
  return (session.idea || session.label || session.prefillIdea || session.workflowGoal || 'Untitled mission')
    .split('\n')[0]
    .replace(/^#+\s*/, '')
    .trim();
}

function Lifecycle({ active = 'Explore' }) {
  const stages = ['Frame', 'Explore', 'Challenge', 'Decide', 'Act', 'Learn'];
  const activeIndex = Math.max(0, stages.indexOf(active));
  return (
    <div className="nx-lifecycle" aria-label={`Mission stage: ${active}`}>
      {stages.map((stage, index) => (
        <React.Fragment key={stage}>
          <span className={`nx-lifecycle-step ${index < activeIndex ? 'is-done' : ''} ${index === activeIndex ? 'is-active' : ''}`}>
            <i />{stage}
          </span>
          {index < stages.length - 1 && <span className={`nx-lifecycle-line ${index < activeIndex ? 'is-done' : ''}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

function MissionCard({ session, onOpen, compact = false }) {
  const mode = MODE_META[session.mode] || MODE_META.idea;
  const stage = lifecycleStage(session);
  const count = session.nodeCount || session.rawNodes?.length || session.nodes?.length || 0;
  return (
    <button className={`nx-mission-card ${compact ? 'is-compact' : ''}`} onClick={() => onOpen(session)}>
      <div className="nx-mission-card-top">
        <span className="nx-mode-pill" style={{ '--nx-mode': mode.color }}>{mode.label}</span>
        <span className="nx-mission-time">{relativeTime(session.updatedAt || session.createdAt)}</span>
      </div>
      <strong>{cleanTitle(session)}</strong>
      {!compact && (
        <p>{session.sessionSummary || session.sessionBrief || 'A working model ready for the next pass.'}</p>
      )}
      <div className="nx-mission-card-foot">
        <span>{count} nodes</span>
        <span className="nx-mission-next">Continue · {stage} <b>→</b></span>
      </div>
    </button>
  );
}

function StartMission({ draft, setDraft, intent, setIntent, onStart, onWorkflow }) {
  return (
    <section className="nx-start" aria-labelledby="nx-start-title">
      <div className="nx-start-kicker">Start with the outcome—not the tool</div>
      <h1 id="nx-start-title">What needs thinking?</h1>
      <p className="nx-start-sub">Bring a decision, opportunity, project, or subject. ThoughtClaw will build the right thinking path.</p>
      <div className="nx-composer">
        <textarea
          value={draft}
          onChange={event => setDraft(event.target.value)}
          placeholder="e.g. Decide whether to enter the European market this year"
          aria-label="What needs thinking?"
          rows={3}
          onKeyDown={event => {
            if ((event.metaKey || event.ctrlKey) && event.key === 'Enter' && draft.trim()) onStart();
          }}
        />
        <div className="nx-intents" role="radiogroup" aria-label="Thinking intent">
          {INTENTS.map(item => (
            <button
              key={item.id}
              className={intent === item.id ? 'is-selected' : ''}
              role="radio"
              aria-checked={intent === item.id}
              onClick={() => setIntent(item.id)}
              type="button"
            >
              <span>{item.icon}</span>
              <span><b>{item.label}</b><small>{item.hint}</small></span>
            </button>
          ))}
        </div>
        <div className="nx-composer-actions">
          <span className="nx-private-note">Private by default · context compounds across missions</span>
          <button className="nx-plan-button" onClick={onWorkflow} disabled={!draft.trim()} type="button">Build a thinking plan</button>
          <button className="nx-start-button" onClick={onStart} disabled={!draft.trim()} type="button">Start mission <span>↗</span></button>
        </div>
      </div>
    </section>
  );
}

function TodayView({ sessions, loading, onOpenSession, onStart, onWorkflow, onOpenClaudeMemory, claudeMemory }) {
  const [draft, setDraft] = useState('');
  const [intent, setIntent] = useState('explore');
  const canLocalScanClaude = typeof window !== 'undefined' && ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
  const recent = sessions.slice(0, 3);
  const totalNodes = sessions.reduce((sum, session) => sum + (session.nodeCount || session.rawNodes?.length || 0), 0);
  const start = () => onStart(draft.trim(), INTENTS.find(item => item.id === intent));
  const workflow = () => onWorkflow(draft.trim(), INTENTS.find(item => item.id === intent));

  return (
    <div className="nx-today">
      <header className="nx-orientation">
        <div>
          <span className="nx-eyebrow">Your thinking system · today</span>
          <h2>{sessions.length ? `${sessions.length} missions are building your strategic memory.` : 'Turn the next important question into a living model.'}</h2>
        </div>
        <div className="nx-orientation-stat"><strong>{totalNodes}</strong><span>connected ideas</span></div>
      </header>

      <button className={`nx-memory-bridge ${claudeMemory ? 'is-active' : ''}`} onClick={onOpenClaudeMemory}>
        <span className="nx-memory-icon">{claudeMemory ? '✓' : '◇'}</span>
        <span><small>{claudeMemory ? 'CLAUDE MEMORY ACTIVE' : 'CONTINUE FROM CLAUDE'}</small><b>{claudeMemory ? `${(claudeMemory.imported?.codeSessions || 0) + (claudeMemory.imported?.coworkSessions || 0)} sessions are available to every new mission.` : canLocalScanClaude ? 'Bring in your Claude Code and Cowork history' : 'Upload or paste Claude history from this Mac'}</b></span>
        <i>{claudeMemory ? 'Review memory' : canLocalScanClaude ? 'Import in two clicks' : 'Open import options'} →</i>
      </button>

      <StartMission
        draft={draft}
        setDraft={setDraft}
        intent={intent}
        setIntent={setIntent}
        onStart={start}
        onWorkflow={workflow}
      />

      <div className="nx-today-grid">
        <section className="nx-section nx-recent">
          <div className="nx-section-head">
            <div><span>Active missions</span><h3>Continue where the model is alive</h3></div>
            <button onClick={() => onOpenSession(null, 'missions')}>View all</button>
          </div>
          <div className="nx-mission-list">
            {loading ? <div className="nx-skeleton">Loading your missions…</div> : recent.length ? recent.map(session => (
              <MissionCard key={session.id || cleanTitle(session)} session={session} onOpen={onOpenSession} />
            )) : (
              <div className="nx-empty">
                <span>⌁</span><strong>No missions yet</strong><p>Your first mission becomes the seed of a compounding knowledge system.</p>
              </div>
            )}
          </div>
        </section>

        <aside className="nx-radar">
          <div className="nx-section-head"><div><span>System radar</span><h3>What changed while you were away</h3></div><i className="nx-live-dot" /></div>
          <div className="nx-radar-event is-signal"><span>01</span><div><b>Evidence</b><p>{sessions.length ? 'Your recent missions are ready for another evidence pass.' : 'Connect a source when live monitoring is available.'}</p></div></div>
          <div className="nx-radar-event is-risk"><span>02</span><div><b>Open assumptions</b><p>{sessions.length ? 'Challenge the strongest branch before committing resources.' : 'Assumptions will surface as your models grow.'}</p></div></div>
          <div className="nx-radar-event is-agent"><span>03</span><div><b>Autonomous activity</b><p>Living trees and dream cycles are quiet until you enable them.</p></div></div>
        </aside>
      </div>
    </div>
  );
}

function MissionsView({ sessions, loading, onOpenSession }) {
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => sessions.filter(session => cleanTitle(session).toLowerCase().includes(query.toLowerCase())), [sessions, query]);
  return (
    <div className="nx-page">
      <header className="nx-page-head">
        <div><span className="nx-eyebrow">Your working models</span><h1>Missions</h1><p>Every mission is a living model—not a disposable chat.</p></div>
        <label className="nx-search"><span>⌕</span><input value={query} onChange={event => setQuery(event.target.value)} placeholder="Search missions" aria-label="Search missions" /></label>
      </header>
      <div className="nx-mission-grid">
        {loading ? <div className="nx-skeleton">Loading your missions…</div> : filtered.length ? filtered.map(session => <MissionCard key={session.id || cleanTitle(session)} session={session} onOpen={onOpenSession} compact />) : (
          <div className="nx-empty nx-empty-wide"><span>⌕</span><strong>No matching missions</strong><p>Try a broader phrase, or start a new mission from Today.</p></div>
        )}
      </div>
    </div>
  );
}

function LibraryView({ onOpenKnowledge, onOpenSettings, onOpenClaudeMemory, claudeMemory }) {
  const items = [
    { icon: claudeMemory ? '✓' : '◇', title: 'Claude memory', meta: claudeMemory ? 'Active across new missions' : 'Claude Code + Cowork continuity', body: claudeMemory ? `${(claudeMemory.imported?.codeSessions || 0) + (claudeMemory.imported?.coworkSessions || 0)} prior sessions can now inform ThoughtClaw.` : 'Import prior chats, decisions, project memory, and artifact history from this Mac.', action: onOpenClaudeMemory },
    { icon: '◇', title: 'Knowledge', meta: 'Cross-session memory', body: 'Explore the concepts and connections ThoughtClaw has retained across your work.', action: onOpenKnowledge },
    { icon: '⌘', title: 'Thinking patterns', meta: 'Reusable reasoning systems', body: 'Inspect, compose, and evolve the methods the system uses to think.', action: () => onOpenSettings('patterns') },
    { icon: '↗', title: 'Integrations', meta: 'Sources and destinations', body: 'Connect Gmail, GitHub, Telegram, live streams, and execution tools.', action: () => onOpenSettings('integrations') },
    { icon: '◉', title: 'System settings', meta: 'Models, prompts, autonomy', body: 'Control providers, prompts, billing, workspaces, and advanced behavior.', action: () => onOpenSettings(null) },
  ];
  return (
    <div className="nx-page">
      <header className="nx-page-head"><div><span className="nx-eyebrow">Compounding assets</span><h1>Library</h1><p>Your knowledge, methods, connections, and system controls.</p></div></header>
      <div className="nx-library-grid">
        {items.map(item => <button key={item.title} className="nx-library-card" onClick={item.action}><span className="nx-library-icon">{item.icon}</span><small>{item.meta}</small><h3>{item.title}</h3><p>{item.body}</p><b>Open <span>→</span></b></button>)}
      </div>
      <section className="nx-library-note"><span>THE COMPOUNDING LOOP</span><p>Every mission writes a brief. Every outcome becomes evidence. Every pattern records what worked. The system gets more useful because you used it—not because you configured it.</p></section>
    </div>
  );
}

export default function NextExperience({
  user,
  logout,
  renderSession,
  renderWorkflow,
  renderKnowledge,
  renderSettings,
}) {
  const [view, setView] = useState('today');
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workspaceSession, setWorkspaceSession] = useState(null);
  const [workflowRequest, setWorkflowRequest] = useState(null);
  const [embeddedView, setEmbeddedView] = useState(null);
  const [claudeMemory, setClaudeMemory] = useState(() => readClaudeMemory());

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      let serverSessions = [];
      try {
        const response = await authFetch(`${API_URL}/api/sessions?limit=50`);
        if (response.ok) serverSessions = await response.json();
      } catch { /* Local history still makes the shell useful offline. */ }
      setSessions(normalizeSessions(serverSessions, readLocalSessions()));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const navigate = useCallback(nextView => {
    setView(nextView);
    setWorkspaceSession(null);
    setWorkflowRequest(null);
    setEmbeddedView(null);
  }, []);

  const openSession = useCallback((session, redirectView) => {
    if (!session && redirectView) return navigate(redirectView);
    setWorkspaceSession(session);
    setWorkflowRequest(null);
    setEmbeddedView(null);
  }, [navigate]);

  const startMission = useCallback((idea, intent) => {
    const memory = readClaudeMemory();
    setWorkspaceSession({ isNew: true, nextExperience: true, mode: intent?.mode || 'idea', prefillIdea: idea, contextPack: memory });
    setWorkflowRequest(null);
  }, []);

  useEffect(() => {
    const handleStartMode = event => {
      const { mode = 'idea', goal = '' } = event.detail || {};
      const memory = readClaudeMemory();
      setWorkspaceSession({ isNew: true, nextExperience: true, mode, prefillIdea: goal, contextPack: memory });
      setWorkflowRequest(null);
      setEmbeddedView(null);
    };
    window.addEventListener('thoughtclaw:start-mode', handleStartMode);
    return () => window.removeEventListener('thoughtclaw:start-mode', handleStartMode);
  }, []);

  const startWorkflow = useCallback((goal, intent) => {
    setWorkflowRequest({ goal, modeId: intent?.mode || 'idea', key: Date.now(), contextPack: readClaudeMemory() });
    setWorkspaceSession(null);
  }, []);

  const closeWork = useCallback(() => {
    setWorkspaceSession(null);
    setWorkflowRequest(null);
    setEmbeddedView(null);
    fetchSessions();
  }, [fetchSessions]);

  const title = workspaceSession ? cleanTitle(workspaceSession) : workflowRequest ? workflowRequest.goal : null;
  const stage = workspaceSession ? lifecycleStage(workspaceSession) : workflowRequest ? 'Frame' : null;

  let content;
  if (workspaceSession) {
    content = renderSession(workspaceSession, closeWork);
  } else if (workflowRequest) {
    content = renderWorkflow(workflowRequest, {
      onReady: session => setWorkspaceSession({ ...session, contextPack: workflowRequest.contextPack }),
      onSkip: () => setWorkspaceSession({ isNew: true, mode: workflowRequest.modeId, prefillIdea: workflowRequest.goal, contextPack: workflowRequest.contextPack }),
      onClose: closeWork,
    });
  } else if (embeddedView?.type === 'knowledge') {
    content = renderKnowledge(() => setEmbeddedView(null));
  } else if (embeddedView?.type === 'settings') {
    content = renderSettings(embeddedView.tab, () => setEmbeddedView(null), openSession);
  } else if (embeddedView?.type === 'claude-memory') {
    content = <ClaudeMemoryImport onClose={() => { setClaudeMemory(readClaudeMemory()); setEmbeddedView(null); }} onStartMission={() => { setClaudeMemory(readClaudeMemory()); setEmbeddedView(null); navigate('today'); }} />;
  } else if (view === 'missions') {
    content = <MissionsView sessions={sessions} loading={loading} onOpenSession={openSession} />;
  } else if (view === 'library') {
    content = <LibraryView claudeMemory={claudeMemory} onOpenClaudeMemory={() => setEmbeddedView({ type: 'claude-memory' })} onOpenKnowledge={() => setEmbeddedView({ type: 'knowledge' })} onOpenSettings={tab => setEmbeddedView({ type: 'settings', tab })} />;
  } else {
    content = <TodayView claudeMemory={claudeMemory} onOpenClaudeMemory={() => setEmbeddedView({ type: 'claude-memory' })} sessions={sessions} loading={loading} onOpenSession={openSession} onStart={startMission} onWorkflow={startWorkflow} />;
  }

  return (
    <div className={`nx-shell ${workspaceSession || workflowRequest || embeddedView ? 'has-workspace' : ''}`}>
      <aside className="nx-rail">
        <button className="nx-brand" onClick={() => navigate('today')} aria-label="ThoughtClaw home"><span>◈</span><b>THOUGHT<br />CLAW</b></button>
        <nav aria-label="Primary navigation">
          {NAV_ITEMS.map(item => <button key={item.id} className={view === item.id && !workspaceSession && !workflowRequest && !embeddedView ? 'is-active' : ''} onClick={() => navigate(item.id)}><span>{item.icon}</span><b>{item.label}</b></button>)}
        </nav>
        <div className="nx-rail-bottom">
          <a href="/classic" title="Open the original ThoughtClaw experience"><span>↙</span><b>Classic</b></a>
          <button onClick={() => setEmbeddedView({ type: 'settings', tab: null })}><span>⚙</span><b>Settings</b></button>
          <button className="nx-avatar" title={user?.email || 'Account'} onClick={logout}>{(user?.displayName || user?.email || 'T').charAt(0).toUpperCase()}</button>
        </div>
      </aside>

      <main className="nx-main">
        <header className="nx-topbar">
          <div className="nx-topbar-context">
            {(workspaceSession || workflowRequest || embeddedView) && <button className="nx-back" onClick={closeWork}>← <span>Command center</span></button>}
            {!title && !embeddedView && <><span className="nx-topbar-dot" /><b>{NAV_ITEMS.find(item => item.id === view)?.label}</b><small>Predictive thinking system</small></>}
            {title && <><span className="nx-topbar-dot is-active" /><b>{title}</b><small>{stage} stage</small></>}
            {embeddedView && <><span className="nx-topbar-dot is-active" /><b>{embeddedView.type === 'knowledge' ? 'Knowledge' : embeddedView.type === 'claude-memory' ? 'Claude memory' : 'Settings'}</b><small>System library</small></>}
          </div>
          {stage && <Lifecycle active={stage} />}
          <div className="nx-topbar-actions"><button aria-label="Search missions" onClick={() => navigate('missions')}>⌕</button><button className="nx-new" onClick={() => navigate('today')}>+ New mission</button></div>
        </header>
        <div className={`nx-content ${workspaceSession || workflowRequest || embeddedView ? 'nx-workspace-frame' : ''}`}>{content}</div>
      </main>

      <nav className="nx-mobile-nav" aria-label="Mobile navigation">
        {NAV_ITEMS.map(item => <button key={item.id} className={view === item.id ? 'is-active' : ''} onClick={() => navigate(item.id)}><span>{item.icon}</span>{item.label}</button>)}
      </nav>
    </div>
  );
}
